import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, getToken, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { StatusBadge, EmptyState } from "@/components/common";
import { PRESTATIONS, fmtDate } from "@/lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";

export default function Report() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [selected, setSelected] = useState(null);
  const [type, setType] = useState("client");
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState([]);
  const [combining, setCombining] = useState(false);

  useEffect(() => { api.get("/visits").then((r) => setVisits(r.data)); }, []);
  useEffect(() => {
    if (id) api.get(`/visits/${id}`).then((r) => setSelected(r.data)).catch(() => {});
  }, [id]);

  const loadPreview = async (visitId, t) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/visits/${visitId}/report`, { params: { type: t }, responseType: "blob" });
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(data));
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  const select = (v) => { setSelected(v); setPdfUrl(null); navigate(`/report/${v.id}`); };

  const generateCombined = async (t) => {
    setCombining(true); setLoading(true);
    try {
      const { data } = await api.post("/reports/combined", { visit_ids: checked, type: t }, { responseType: "blob" });
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
      setPdfUrl(URL.createObjectURL(data));
    } catch (e) { toast.error(apiError(e)); } finally { setCombining(false); setLoading(false); }
  };

  const startCombined = () => { setPdfUrl(null); setSelected({ id: "__combined__", _combined: true, reference: "Dossier multi-sites", client_name: `${checked.length} visite(s)`, site_name: "Rapport groupé (tous sites)", status: "" }); };
  const toggleCheck = (vid) => setChecked((c) => c.includes(vid) ? c.filter((x) => x !== vid) : [...c, vid]);

  useEffect(() => {
    if (!selected) return;
    if (selected._combined) generateCombined(type);
    else loadPreview(selected.id, type);
    /* eslint-disable-next-line */
  }, [selected?.id, type]);

  const download = async () => {
    try {
      const resp = selected._combined
        ? await api.post("/reports/combined", { visit_ids: checked, type }, { responseType: "blob" })
        : await api.get(`/visits/${selected.id}/report`, { params: { type }, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const draft = !selected._combined && selected.status !== "validee" ? "_brouillon" : "";
      a.download = selected._combined ? `dossier_multi_sites_${type}.pdf` : `${selected.reference}_${type}${draft}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Rapport téléchargé");
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportXlsx = async () => {
    try {
      const { data } = await api.get(`/visits/${selected.id}/synthesis.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selected.reference}_synthese.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Synthèse exportée (tableur)");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="report-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 lg:text-3xl">SIGNA REPORT</h1>
        <p className="text-sm text-slate-500">Générez les rapports de visite avant devis à partir des relevés.</p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
        <div className="space-y-2">
          <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-slate-500">Visites</h2>
          {visits.length === 0 ? (
            <EmptyState icon="FileText" title="Aucune visite" testId="report-empty" />
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-sm">
                <span className="text-slate-600" data-testid="selection-count">{checked.length} sélectionnée(s)</span>
                <div className="flex items-center gap-2">
                  {checked.length > 0 && <button onClick={() => setChecked([])} className="text-xs text-slate-500 hover:underline" data-testid="clear-selection">Effacer</button>}
                  <Button size="sm" disabled={checked.length < 1 || combining} onClick={startCombined} className="bg-emerald-600 hover:bg-emerald-700" data-testid="combined-report-btn">
                    <Icons.Layers className="mr-1.5 h-4 w-4" /> Rapport groupé
                  </Button>
                </div>
              </div>
              {visits.map((v) => (
                <div key={v.id} className={`flex items-start gap-2 rounded-xl border p-3 transition-colors ${selected?.id === v.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <input type="checkbox" checked={checked.includes(v.id)} onChange={() => toggleCheck(v.id)} className="mt-1 h-4 w-4 shrink-0 accent-emerald-600" data-testid={`select-visit-${v.reference}`} />
                  <button onClick={() => select(v)} data-testid={`report-visit-${v.reference}`} className="min-w-0 flex-1 text-left">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono-data text-xs text-slate-400">{v.reference}</span>
                      <StatusBadge status={v.status} />
                    </div>
                    <p className="mt-1 truncate font-medium text-slate-800">{v.client_name}</p>
                    <p className="truncate text-sm text-slate-500">{v.site_name} · {fmtDate(v.date)}</p>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          {!selected ? (
            <EmptyState icon="FileText" title="Sélectionnez une visite" testId="report-none-selected">
              Choisissez une visite dans la liste pour prévisualiser et télécharger son rapport.
            </EmptyState>
          ) : (
            <Card className="overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4">
                <div>
                  <p className="font-semibold text-slate-800">{selected.reference} · {selected.client_name}</p>
                  <p className="text-sm text-slate-500">{selected.site_name}</p>
                </div>
                {selected.status !== "validee" && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700" data-testid="draft-notice">
                    <Icons.AlertTriangle className="h-3.5 w-3.5" /> Rapport en BROUILLON (visite non validée)
                  </span>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-4">
                <div className="flex rounded-lg border border-slate-200 p-0.5">
                  <button onClick={() => setType("client")} data-testid="report-type-client"
                    className={`rounded-md px-3 py-1.5 text-sm font-medium ${type === "client" ? "bg-emerald-600 text-white" : "text-slate-600"}`}>Rapport client</button>
                  <button onClick={() => setType("interne")} data-testid="report-type-interne"
                    className={`rounded-md px-3 py-1.5 text-sm font-medium ${type === "interne" ? "bg-emerald-600 text-white" : "text-slate-600"}`}>Synthèse interne</button>
                </div>
                <p className="text-xs text-slate-400">
                  {type === "client" ? "Exclut systématiquement les notes internes." : "Inclut les notes internes de chiffrage."}
                </p>
                <Button onClick={download} className="ml-auto bg-emerald-600 hover:bg-emerald-700" data-testid="report-download">
                  <Icons.Download className="mr-1.5 h-4 w-4" /> Télécharger PDF
                </Button>
                {!selected._combined && (
                  <Button onClick={exportXlsx} variant="outline" data-testid="report-export-xlsx">
                    <Icons.Sheet className="mr-1.5 h-4 w-4" /> Export tableur
                  </Button>
                )}
              </div>

              <div className="bg-slate-100 p-2">
                {loading ? (
                  <div className="flex h-[70vh] items-center justify-center text-slate-400"><Icons.Loader2 className="mr-2 h-5 w-5 animate-spin" />Génération du PDF…</div>
                ) : pdfUrl ? (
                  <iframe title="Aperçu du rapport" src={pdfUrl} className="h-[70vh] w-full rounded-lg bg-white" data-testid="report-preview" />
                ) : (
                  <div className="flex h-[70vh] items-center justify-center text-slate-400">Aperçu indisponible</div>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
